import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Avatar,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Badge
} from '@mui/material';
import {
  TrendingUp as WinsIcon,
  TrendingDown as LossesIcon,
  Remove as DrawsIcon,
  CalendarToday as CalendarIcon,
  Download as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
  Link as LinkIcon,
  Event as EventIcon,
  PictureAsPdf as PdfIcon,
  SportsSoccer as GamesIcon,
  LocationOn as LocationIcon,
  Forum as ChatIcon
} from '@mui/icons-material';
import { useParams, useNavigate, Link } from 'react-router-dom';
import unifiedApi, { buildApiUrl } from '../utils/unifiedApi';
import { useAuth } from '../contexts/AuthContext';
import { useCanManageTeam, useCanEditTeamDetails } from '../contexts/PermissionsContext';
import { useChat } from '../contexts/ChatContext';
import { formatGameDateTime } from '../utils/dateUtils';
import { formatJerseyNumber } from '../utils/formatUtils';
import SEOMetadata from '../components/SEOMetadata';
import TeamLeadingScorers from '../components/team/TeamLeadingScorers';
import TeamDetailsEdit from '../components/team/TeamDetailsEdit';
import TeamRosterManage from '../components/team/TeamRosterManage';
import GameAvailabilityTable from '../components/rsvp/GameAvailabilityTable';

const TeamDetail = () => {
  const { teamSlug } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const _mode = user ? user.role : 'public';
  const [teamData, setTeamData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [calendarMenuAnchor, setCalendarMenuAnchor] = useState(null);
  const [isEditTeamOpen, setIsEditTeamOpen] = useState(false);
  const [isRosterManageOpen, setIsRosterManageOpen] = useState(false);

  const canManageTeam = useCanManageTeam(teamData?.teamId || 0);
  const canEditTeamDetails = useCanEditTeamDetails(teamData?.teamId || 0);
  const { channels: chatChannels } = useChat();
  const teamChatChannel = teamData?.teamId
    ? chatChannels.find((channel) => channel.teamId === teamData.teamId)
    : null;

  const loadTeamData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await unifiedApi.getTeam(teamSlug);
      
      if (!data) {
        setError('Team not found or not public');
        return;
      }

      setTeamData(data);
    } catch (err) {
      console.error('Error loading team details:', err);
      setError('Failed to load team details');
    } finally {
      setLoading(false);
    }
  }, [teamSlug]);

  useEffect(() => {
    if (teamSlug) {
      loadTeamData();
    }
  }, [teamSlug, loadTeamData]);

  const downloadGameSheet = async (gameId, teamId) => {
    try {
      const blob = await unifiedApi.downloadGameSheet(gameId, teamId);
      
      if (blob) {
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `gamesheet-${gameId}-${teamId}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(downloadUrl);
      } else {
        console.error('Failed to download game sheet');
      }
    } catch (error) {
      console.error('Error downloading game sheet:', error);
    }
  };

  // Helper function to handle team navigation using actual team slug
  const handleTeamClick = (teamSlug) => {
    if (teamSlug) {
      navigate(`/teams/${teamSlug}`);
    }
  };

  // Calendar functions
  const handleCalendarMenuOpen = (event) => {
    setCalendarMenuAnchor(event.currentTarget);
  };

  const handleCalendarMenuClose = () => {
    setCalendarMenuAnchor(null);
  };

  const getCalendarUrl = () => {
    const API_BASE_URL = import.meta.env.REACT_APP_API_BASE_URL || import.meta.env.REACT_APP_API_URL || 'http://localhost:5131';
    return `${API_BASE_URL}/api/teams/${teamData.teamId}/calendar.ics`;
  };

  const getCalendarDownloadUrl = () => {
    return getCalendarUrl();
  };

  const handleCopyCalendarLink = async () => {
    try {
      await navigator.clipboard.writeText(getCalendarUrl());
      console.log('Calendar link copied to clipboard');
    } catch (err) {
      console.error('Failed to copy calendar link:', err);
    }
    handleCalendarMenuClose();
  };

  const handleAddToOutlook = () => {
    const calendarUrl = encodeURIComponent(getCalendarUrl());
    const outlookUrl = `https://outlook.live.com/calendar/0/addcalendar?url=${calendarUrl}`;
    window.open(outlookUrl, '_blank');
    handleCalendarMenuClose();
  };

  const handleDownloadCalendar = () => {
    const link = document.createElement('a');
    link.href = getCalendarDownloadUrl();
    link.download = `${teamData.teamName.replace(/\s+/g, '-')}-schedule.ics`;
    link.click();
    handleCalendarMenuClose();
  };

  const handleDownloadBulkGameSheets = async () => {
    try {
      const blob = await unifiedApi.downloadBulkGameSheets(teamSlug);
      
      if (blob) {
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `${teamData.teamName.replace(/\s+/g, '-')}-gamesheets.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(downloadUrl);
      } else {
        console.error('Failed to download bulk game sheets');
      }
    } catch (error) {
      console.error('Error downloading bulk game sheets:', error);
    }
  };

  // Helper function to determine if text should be light or dark based on background color
  const getContrastColor = (hexColor) => {
    if (!hexColor) return '#000000';
    
    // Remove # if present
    const color = hexColor.replace('#', '');
    
    // Parse r, g, b values
    const r = parseInt(color.substr(0, 2), 16);
    const g = parseInt(color.substr(2, 2), 16);
    const b = parseInt(color.substr(4, 2), 16);
    
    // Calculate luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    return luminance > 0.5 ? '#000000' : '#ffffff';
  };

  // Jersey Icon Component
  const JerseyIcon = ({ color = '#ffffff', size = 60 }) => (
    <svg width={size} height={size} viewBox="0 0 100 100">
      <path
        d="M20 30 L20 20 Q20 15 25 15 L35 15 Q40 10 60 10 Q65 15 75 15 Q80 15 80 20 L80 30 L85 35 L85 90 Q85 95 80 95 L20 95 Q15 95 15 90 L15 35 Z"
        fill={color}
        stroke="#333"
        strokeWidth="2"
      />
    </svg>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box mt={4}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!teamData) {
    return (
      <Box mt={4}>
        <Alert severity="info">No team data available</Alert>
      </Box>
    );
  }

  // The API returns a PublicTeamDto directly, not nested objects
  const _team = {
    id: teamData.teamId,
    name: teamData.teamName,
    leagueName: teamData.leagueName
  };
  const standings = teamData.stats;
  const recentGames = teamData.recentGames || [];
  const allUpcomingGames = teamData.upcomingGames || [];
  const roster = teamData.roster || [];

  // Filter upcoming games to only show future games
  const now = new Date();
  const upcomingGames = allUpcomingGames.filter(game => new Date(game.start) > now);

  return (
    <Box>
      {(canEditTeamDetails || canManageTeam) && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            flexWrap: 'wrap',
            gap: 1.5,
            mb: 2
          }}
        >
          {canEditTeamDetails && (
            <Button
              variant="contained"
              size="small"
              onClick={() => setIsEditTeamOpen(true)}
              sx={{ textTransform: 'none' }}
            >
              Edit Team Details
            </Button>
          )}
          {canManageTeam && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => setIsRosterManageOpen(true)}
              sx={{ textTransform: 'none', display: { xs: 'none', md: 'inline-flex' } }}
            >
              Edit Roster
            </Button>
          )}
        </Box>
      )}

      {teamData && (
        <SEOMetadata 
          title={`${teamData.teamName} - Team Details`}
          description={`Team information, roster, statistics and schedule for ${teamData.teamName} in ${teamData.leagueName}`}
          keywords={`${teamData.teamName}, ${teamData.leagueName}, team, roster, soccer, football, schedule, statistics`}
        />
      )}

      {/* Team Header - Styled like PublicTeamDetail */}
      <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' }, 
          gap: { xs: 2, sm: 3 }, 
          mb: 2 
        }}>
          {/* Team Logo and Name - Mobile Layout */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2,
            width: { xs: '100%', sm: 'auto' }
          }}>
            <Avatar
              src={teamData?.logoUrl 
                ? (teamData.logoUrl.startsWith('http') ? teamData.logoUrl : buildApiUrl(teamData.logoUrl)) 
                : ''}
              sx={{ 
                width: { xs: 60, sm: 80 }, 
                height: { xs: 60, sm: 80 }, 
                fontSize: { xs: '1.5rem', sm: '2rem' },
                flexShrink: 0
              }}
            >
              {teamData?.teamName?.charAt(0)?.toUpperCase()}
            </Avatar>

            {/* Team Name for Mobile */}
            <Box sx={{ display: { xs: 'block', sm: 'none' }, flex: 1, minWidth: 0 }}>
              <Typography variant="h5" component="h1" sx={{ wordBreak: 'break-word' }}>
                {teamData.teamName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {teamData.leagueName}
              </Typography>
            </Box>
          </Box>

          {/* Team Name and Details - Desktop */}
          <Box sx={{ flex: 1, minWidth: 0, display: { xs: 'none', sm: 'block' } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Typography variant="h4" component="h1" sx={{ wordBreak: 'break-word' }}>
                {teamData.teamName}
              </Typography>
            </Box>
            
            <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
              {teamData.leagueName}
            </Typography>
            
            {/* Jersey Color Info */}
            {teamData?.jerseyColorHex && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Jersey:
                </Typography>
                <Chip
                  label={teamData.jerseyColorName || 'Unknown'}
                  size="small"
                  onClick={() => {/* do nothing */}}
                  sx={{
                    backgroundColor: teamData.jerseyColorHex,
                    color: getContrastColor(teamData.jerseyColorHex),
                    border: '1px solid #ccc'
                  }}
                />             
              </Box>
            )}
          </Box>

          {/* Jersey Preview - Desktop Only */}
          <Box sx={{ 
            textAlign: 'center',
            display: { xs: 'none', sm: 'block' },
            flexShrink: 0
          }}>
            <Typography variant="caption" display="block" color="text.secondary" mb={1}>
              Team Jersey
            </Typography>
            <JerseyIcon color={teamData?.jerseyColorHex || '#ffffff'} size={80} />
          </Box>
        </Box>

        {/* Mobile: Jersey Color Info */}
        {teamData?.jerseyColorHex && (
          <Box sx={{ 
            display: { xs: 'flex', sm: 'none' }, 
            alignItems: 'center', 
            gap: 1, 
            mb: 2 
          }}>
            <Typography variant="body2" color="text.secondary">
              Jersey:
            </Typography>
            <Chip
              label={teamData.jerseyColorName || 'Unknown'}
              size="small"
              onClick={() => {/* do nothing */}}
              sx={{
                backgroundColor: teamData.jerseyColorHex,
                color: getContrastColor(teamData.jerseyColorHex),
                border: '1px solid #ccc'
              }}
            />
            <Box sx={{ ml: 'auto', flexShrink: 0 }}>
              <JerseyIcon color={teamData?.jerseyColorHex || '#ffffff'} size={50} />
            </Box>
          </Box>
        )}

        {/* Team Calendar - Responsive Buttons */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' }, 
          gap: 1, 
          mt: { xs: 0, sm: 1 } 
        }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<CalendarIcon />}
            endIcon={<ExpandMoreIcon />}
            onClick={handleCalendarMenuOpen}
            sx={{
              textTransform: 'none',
              borderColor: '#2E4156',
              color: '#2E4156',
              '&:hover': {
                borderColor: '#1a2530',
                backgroundColor: 'rgba(46, 65, 86, 0.04)'
              }
            }}
          >
            Calendar
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<PdfIcon />}
            onClick={handleDownloadBulkGameSheets}
            sx={{
              textTransform: 'none',
              backgroundColor: '#2E4156',
              color: '#ffffff',
              '&:hover': {
                backgroundColor: '#1a2530'
              }
            }}
          >
            <Box component="span" sx={{ 
              display: { xs: 'none', sm: 'inline' }
            }}>
              Download All Game Sheets
            </Box>
            <Box component="span" sx={{ 
              display: { xs: 'inline', sm: 'none' }
            }}>
              All Game Sheets
            </Box>
          </Button>
          {teamChatChannel && (
            <Button
              variant="contained"
              size="small"
              color="secondary"
              component={Link}
              to={`/chat/${teamChatChannel.id}`}
              startIcon={(
                <Badge variant="dot" color="error" invisible={!teamChatChannel.hasNewMessages}>
                  <ChatIcon />
                </Badge>
              )}
              sx={{ textTransform: 'none' }}
            >
              Team Chat
            </Button>
          )}
          <Menu
            anchorEl={calendarMenuAnchor}
            open={Boolean(calendarMenuAnchor)}
            onClose={handleCalendarMenuClose}
          >
            <MenuItem onClick={handleCopyCalendarLink}>
              <ListItemIcon>
                <LinkIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Copy Calendar Link" />
            </MenuItem>
            <MenuItem onClick={handleAddToOutlook}>
              <ListItemIcon>
                <EventIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Add to Outlook" />
            </MenuItem>
            <MenuItem onClick={handleDownloadCalendar}>
              <ListItemIcon>
                <DownloadIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Download .ics File" />
            </MenuItem>
          </Menu>
        </Box>
      </Paper>

      {/* Team Stats Cards */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 2, 
        mb: 4 
      }}>
        <Card sx={{ textAlign: 'center', p: 2, flex: 1 }}>
          <CardContent sx={{ pb: '16px !important' }}>
            <Typography variant="h4" color="primary" fontWeight="bold">
              {standings?.points || 0}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Points
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ textAlign: 'center', p: 2, flex: 1 }}>
          <CardContent sx={{ pb: '16px !important' }}>
            <Typography variant="h4" color="primary" fontWeight="bold">
              {standings?.gamesPlayed || 0}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Games Played
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ textAlign: 'center', p: 2, flex: 1 }}>
          <CardContent sx={{ pb: '16px !important' }}>
            <Typography variant="h4" color={standings?.goalDifference >= 0 ? 'success.main' : 'error.main'} fontWeight="bold">
              {standings?.goalDifference >= 0 ? '+' : ''}{standings?.goalDifference || 0}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Goal Difference
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ textAlign: 'center', p: 2, flex: 1 }}>
          <CardContent sx={{ pb: '16px !important' }}>
            <Typography variant="h4" color="primary" fontWeight="bold">
              {standings?.winPercentage ? `${standings.winPercentage.toFixed(1)}%` : '0%'}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Win Rate
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Season Record */}
      {standings && (
        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ color: '#2E4156', fontWeight: 'bold' }}>
            Season Record
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap" gap={2}>
            <Chip
              icon={<WinsIcon />}
              label={`${standings.wins || 0} Wins`}
              color="success"
              variant="filled"
              size="medium"
              clickable={false}
              onClick={() => {/* do nothing */}}
            />
            <Chip
              icon={<DrawsIcon />}
              label={`${standings.draws || 0} Draws`}
              variant="filled"
              size="medium"
              clickable={false}
              onClick={() => {/* do nothing */}}
              sx={{ 
                backgroundColor: '#ff9800', 
                color: 'white',
                '& .MuiChip-icon': { color: 'white' }
              }}
            />
            <Chip
              icon={<LossesIcon />}
              label={`${standings.losses || 0} Losses`}
              color="error"
              variant="filled"
              size="medium"
              clickable={false}
              onClick={() => {/* do nothing */}}
            />
            <Chip
              icon={<GamesIcon />}
              label={`${standings.goalsFor || 0} GF`}
              variant="outlined"
              size="medium"
              clickable={false}
              onClick={() => {/* do nothing */}}
              sx={{ color: '#2E4156', borderColor: '#2E4156' }}
            />
            <Chip
              icon={<GamesIcon />}
              label={`${standings.goalsAgainst || 0} GA`}
              variant="outlined"
              size="medium"
              clickable={false}
              onClick={() => {/* do nothing */}}
              sx={{ color: '#2E4156', borderColor: '#2E4156' }}
            />
          </Stack>
        </Paper>
      )}

      {/* Team Leading Scorers */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <TeamLeadingScorers 
          teamSlug={teamSlug}
          limit={15}
          minimumGoals={0}
          title="Top Goal Scorers"
          isPublic={true}
          hidePieChartOnMobile={true}
        />
      </Paper>

      {/* Team Roster */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ color: '#2E4156', fontWeight: 'bold' }}>
          Team Roster
        </Typography>
        
        {/* Manager Line */}
        {roster.filter(player => player.role === 'team-manager').length > 0 && (
          <Typography variant="h6" sx={{ color: '#2E4156', fontWeight: 'bold', mb: 3 }}>
            Manager: {roster
              .filter(player => player.role === 'team-manager')
              .map((manager, idx) => {
                const managerName = `${manager.firstName || ''} ${manager.lastName || manager.playerName || ''}`.trim();
                if (isAuthenticated && manager.userTenantId) {
                  return (
                    <Typography
                      key={idx}
                      component={Link}
                      to={`/player/${manager.userTenantId}`}
                      sx={{
                        textDecoration: 'none',
                        color: 'primary.main',
                        '&:hover': {
                          textDecoration: 'underline'
                        }
                      }}
                    >
                      {managerName}
                    </Typography>
                  );
                }
                return managerName;
              })
              .reduce((prev, curr, idx) => idx === 0 ? [curr] : [...prev, ', ', curr], [])}
          </Typography>
        )}
        
        {roster && roster.length > 0 ? (
          (() => {
            // Group players by position
            const playerGroups = roster              
              .reduce((groups, player) => {
                const position = player.position || 'UNASSIGNED';
                if (!groups[position]) {
                  groups[position] = [];
                }
                groups[position].push(player);
                return groups;
              }, {});

            // Check if all players are unassigned
            const allPlayersUnassigned = Object.keys(playerGroups).length === 1 && Object.keys(playerGroups)[0] === 'UNASSIGNED';

            // If all players are unassigned, show a simple table
            if (allPlayersUnassigned) {
              return (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Jersey #</TableCell>
                        <TableCell>Player Name</TableCell>
                        <TableCell>Position</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {roster
                        .filter(player => player.role !== 'team-manager')
                        .sort((a, b) => (a.jerseyNumber || 99) - (b.jerseyNumber || 99))
                        .map((player) => (
                        <TableRow key={player.playerId}>
                          <TableCell>
                            <Typography variant="body2" fontWeight="bold">
                              {formatJerseyNumber(player.jerseyNumber) || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {isAuthenticated && player.userTenantId ? (
                              <Typography
                                component={Link}
                                to={`/player/${player.userTenantId}`}
                                sx={{
                                  textDecoration: 'none',
                                  color: 'inherit',
                                  '&:hover': {
                                    color: 'primary.main',
                                    textDecoration: 'underline'
                                  }
                                }}
                              >
                                {player.playerName || `${player.firstName || ''} ${player.lastName || ''}`.trim()}
                                {player.playerStatus === 'Injured' && (
                                  <Typography component="span" variant="caption" color="error" sx={{ ml: 1 }}>
                                    (Injured)
                                  </Typography>
                                )}
                                {player.membershipType === 'LongTermSpare' && (
                                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                    (Spare)
                                  </Typography>
                                )}
                              </Typography>
                            ) : (
                              <>
                                {player.playerName || `${player.firstName || ''} ${player.lastName || ''}`.trim()}
                                {player.playerStatus === 'Injured' && (
                                  <Typography component="span" variant="caption" color="error" sx={{ ml: 1 }}>
                                    (Injured)
                                  </Typography>
                                )}
                                {player.membershipType === 'LongTermSpare' && (
                                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                    (Spare)
                                  </Typography>
                                )}
                              </>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="textSecondary">
                              {player.position || '-'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              );
            }

            // Otherwise, show position boxes as in your screenshot
            // Define position order priority (common soccer positions)
            const positionOrder = ['GK', 'FB', 'CB', 'LB', 'RB', 'DM', 'CM', 'MF', 'LM', 'RM', 'AM', 'LW', 'RW', 'CF', 'FW', 'ST'];
            
            // Sort positions by priority, putting known positions first
            const sortedPositions = Object.keys(playerGroups).sort((a, b) => {
              const aIndex = positionOrder.indexOf(a);
              const bIndex = positionOrder.indexOf(b);
              
              // If both positions are in the order array, sort by their index
              if (aIndex !== -1 && bIndex !== -1) {
                return aIndex - bIndex;
              }
              
              // If only one is in the array, prioritize it
              if (aIndex !== -1) return -1;
              if (bIndex !== -1) return 1;
              
              // If neither is in the array, sort alphabetically
              return a.localeCompare(b);
            });

            return (
              <Box 
                sx={{ 
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 2,
                  alignItems: 'stretch'
                }}
              >
                {sortedPositions.map((position) => (
                  <Box
                    key={position}
                    sx={{
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      p: 2,
                      backgroundColor: '#f9f9f9',
                      flex: {
                        xs: '1 1 100%',  // Full width on mobile
                        sm: sortedPositions.length === 1 ? '1 1 100%' : 
                            sortedPositions.length === 2 ? '1 1 calc(50% - 8px)' : 
                            '1 1 calc(33.33% - 11px)',  // 1-3 columns on small
                        md: sortedPositions.length === 1 ? '1 1 100%' :
                            sortedPositions.length === 2 ? '1 1 calc(50% - 8px)' :
                            sortedPositions.length === 3 ? '1 1 calc(33.33% - 11px)' :
                            sortedPositions.length === 4 ? '1 1 calc(25% - 12px)' :
                            '1 1 calc(20% - 13px)'  // Up to 5 columns on medium+
                      },
                      minWidth: '180px'  // Slightly smaller minimum width
                    }}
                  >
                    <Typography 
                      variant="subtitle1" 
                      fontWeight="bold" 
                      sx={{ mb: 2, textAlign: 'center', color: '#2E4156' }}
                    >
                      {position}
                    </Typography>
                    <Stack spacing={1}>
                      {playerGroups[position]
                        .sort((a, b) => (a.jerseyNumber || 99) - (b.jerseyNumber || 99))
                        .map((player) => (
                        <Typography 
                          key={player.playerId} 
                          variant="body2"
                          sx={{ 
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            flexWrap: 'wrap'
                          }}
                        >
                          {isAuthenticated && player.userTenantId ? (
                            <Typography
                              component={Link}
                              to={`/player/${player.userTenantId}`}
                              variant="body2"
                              sx={{
                                textDecoration: 'none',
                                color: 'inherit',
                                '&:hover': {
                                  color: 'primary.main',
                                  textDecoration: 'underline'
                                }
                              }}
                            >
                              {player.jerseyNumber ? `${formatJerseyNumber(player.jerseyNumber)} - ` : ''}
                              {player.playerName || `${player.firstName || ''} ${player.lastName || ''}`.trim()}
                              {player.playerStatus === 'Injured' && (
                                <Typography component="span" variant="caption" color="error" sx={{ ml: 0.5 }}>
                                  (Injured)
                                </Typography>
                              )}
                              {player.membershipType === 'LongTermSpare' && (
                                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                                  (Spare)
                                </Typography>
                              )}
                            </Typography>
                          ) : (
                            <span>
                              {player.jerseyNumber ? `${formatJerseyNumber(player.jerseyNumber)} - ` : ''}
                              {player.playerName || `${player.firstName || ''} ${player.lastName || ''}`.trim()}
                              {player.playerStatus === 'Injured' && (
                                <Typography component="span" variant="caption" color="error" sx={{ ml: 0.5 }}>
                                  (Injured)
                                </Typography>
                              )}
                              {player.membershipType === 'LongTermSpare' && (
                                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                                  (Spare)
                                </Typography>
                              )}
                            </span>
                          )}
                        </Typography>
                      ))}
                    </Stack>
                  </Box>
                ))}
              </Box>
            );
          })()
        ) : (
          <Alert severity="info">No roster information available</Alert>
        )}
      </Paper>

      {/* Game Availability Table - Only for authenticated users */}
      {isAuthenticated && teamData && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <GameAvailabilityTable 
            teamId={teamData.teamId} 
            leagueId={teamData.leagueId} 
          />
        </Paper>
      )}

      {/* Games Section - Responsive Layout */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', md: 'row' },
        gap: 3, 
        mb: 4 
      }}>
        {/* Upcoming Games */}
        <Paper sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5" sx={{ color: '#2E4156', fontWeight: 'bold' }}>
              Upcoming Games
            </Typography>
          </Box>
          
          {upcomingGames && upcomingGames.length > 0 ? (
            <>
              {/* Desktop Table View */}
              <TableContainer sx={{ flex: 1, display: { xs: 'none', md: 'block' } }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Opponent</TableCell>
                      <TableCell>Venue</TableCell>
                      <TableCell>Game Sheet</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {upcomingGames.slice(0, 5).map((game) => (
                      <TableRow key={game.gameId}>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <CalendarIcon fontSize="small" color="action" />
                            <Typography variant="body2">
                              {formatGameDateTime(game.start)}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="text"
                            size="small"
                            onClick={() => handleTeamClick(game.homeTeamId === teamData.teamId ? game.awayTeamSlug : game.homeTeamSlug)}
                            sx={{ 
                              textTransform: 'none',
                              color: 'primary.main',
                              fontWeight: 'bold'
                            }}
                          >
                            {game.homeTeamId === teamData.teamId ? `vs ${game.awayTeamName}` : `@ ${game.homeTeamName}`}
                          </Button>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <LocationIcon fontSize="small" color="action" />
                            <Box>
                              <Typography variant="body2">
                                {game.venueName}
                              </Typography>
                              {game.fieldName && (
                                <Typography variant="caption" color="textSecondary">
                                  {game.fieldName}
                                </Typography>
                              )}
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Download Game Sheet">
                            <IconButton
                              size="small"
                              onClick={() => downloadGameSheet(game.gameId, teamData.teamId)}
                              sx={{ color: '#2E4156' }}
                            >
                              <DownloadIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Mobile Card View */}
              <Stack spacing={2} sx={{ display: { xs: 'flex', md: 'none' } }}>
                {upcomingGames.slice(0, 2).map((game) => (
                  <Card key={game.gameId} variant="outlined">
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Stack spacing={1.5}>
                        {/* Date */}
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <CalendarIcon fontSize="small" color="action" />
                          <Typography variant="body2" fontWeight="bold">
                            {formatGameDateTime(game.start)}
                          </Typography>
                        </Stack>

                        {/* Opponent */}
                        <Button
                          variant="outlined"
                          fullWidth
                          size="small"
                          onClick={() => handleTeamClick(game.homeTeamId === teamData.teamId ? game.awayTeamSlug : game.homeTeamSlug)}
                          sx={{ 
                            textTransform: 'none',
                            justifyContent: 'flex-start',
                            fontWeight: 'bold'
                          }}
                        >
                          {game.homeTeamId === teamData.teamId ? `vs ${game.awayTeamName}` : `@ ${game.homeTeamName}`}
                        </Button>

                        {/* Venue */}
                        <Stack direction="row" alignItems="flex-start" spacing={1}>
                          <LocationIcon fontSize="small" color="action" sx={{ mt: 0.25 }} />
                          <Box flex={1}>
                            <Typography variant="body2">
                              {game.venueName}
                            </Typography>
                            {game.fieldName && (
                              <Typography variant="caption" color="textSecondary">
                                {game.fieldName}
                              </Typography>
                            )}
                          </Box>
                        </Stack>

                        {/* Game Sheet */}
                        <Button
                          variant="contained"
                          fullWidth
                          startIcon={<DownloadIcon />}
                          size="small"
                          onClick={() => downloadGameSheet(game.gameId, teamData.teamId)}
                          sx={{ mt: 1 }}
                        >
                          Download Game Sheet
                        </Button>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </>
          ) : (
            <Alert severity="info">No upcoming games scheduled</Alert>
          )}
        </Paper>

        {/* Recent Results */}
        <Paper sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h5" gutterBottom sx={{ color: '#2E4156', fontWeight: 'bold' }}>
            Recent Results
          </Typography>
          
          {recentGames && recentGames.length > 0 ? (
            <>
              {/* Desktop Table View */}
              <TableContainer sx={{ flex: 1, display: { xs: 'none', md: 'block' } }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Opponent</TableCell>
                      <TableCell>Result</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentGames.slice(0, 5).map((game) => (
                      <TableRow key={game.gameId}>
                        <TableCell>
                          {formatGameDateTime(game.start)}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="text"
                            size="small"
                            onClick={() => handleTeamClick(game.homeTeamId === teamData.teamId ? game.awayTeamSlug : game.homeTeamSlug)}
                            sx={{ textTransform: 'none' }}
                          >
                            {game.homeTeamId === teamData.teamId ? `vs ${game.awayTeamName}` : `@ ${game.homeTeamName}`}
                          </Button>
                        </TableCell>
                        <TableCell>
                          {game.homeScore !== null && game.awayScore !== null ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography 
                                variant="body2" 
                                color={
                                  (game.homeTeamId === teamData.teamId && game.homeScore > game.awayScore) ||
                                  (game.awayTeamId === teamData.teamId && game.awayScore > game.homeScore)
                                    ? 'success.main'
                                    : game.homeScore === game.awayScore
                                    ? 'warning.main'
                                    : 'error.main'
                                }
                              >
                                {game.homeTeamId === teamData.teamId ? `${game.homeScore}-${game.awayScore}` : `${game.awayScore}-${game.homeScore}`}
                              </Typography>
                              <Chip
                                label={
                                  (game.homeTeamId === teamData.teamId && game.homeScore > game.awayScore) ||
                                  (game.awayTeamId === teamData.teamId && game.awayScore > game.homeScore)
                                    ? 'W'
                                    : game.homeScore === game.awayScore
                                    ? 'D'
                                    : 'L'
                                }
                                size="small"
                                onClick={() => {}}
                                sx={{
                                  backgroundColor: 
                                    (game.homeTeamId === teamData.teamId && game.homeScore > game.awayScore) ||
                                    (game.awayTeamId === teamData.teamId && game.awayScore > game.homeScore)
                                      ? '#4caf50'
                                      : game.homeScore === game.awayScore
                                      ? '#ff9800'
                                      : '#f44336',
                                  color: 'white',
                                  fontWeight: 'bold',
                                  minWidth: '24px'
                                }}
                              />
                            </Box>
                          ) : (
                            <Typography variant="body2" color="textSecondary">
                              -
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Mobile Card View */}
              <Stack spacing={2} sx={{ display: { xs: 'flex', md: 'none' } }}>
                {recentGames.slice(0, 5).map((game) => {
                  const isHomeTeam = game.homeTeamId === teamData.teamId;
                  const teamScore = isHomeTeam ? game.homeScore : game.awayScore;
                  const opponentScore = isHomeTeam ? game.awayScore : game.homeScore;
                  const isWin = teamScore > opponentScore;
                  const isDraw = teamScore === opponentScore;
                  const resultLabel = isWin ? 'W' : isDraw ? 'D' : 'L';
                  const resultColor = isWin ? '#4caf50' : isDraw ? '#ff9800' : '#f44336';

                  return (
                    <Card key={game.gameId} variant="outlined">
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Stack spacing={1.5}>
                          {/* Date */}
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <CalendarIcon fontSize="small" color="action" />
                            <Typography variant="body2">
                              {formatGameDateTime(game.start)}
                            </Typography>
                          </Stack>

                          {/* Opponent */}
                          <Button
                            variant="outlined"
                            fullWidth
                            size="small"
                            onClick={() => handleTeamClick(isHomeTeam ? game.awayTeamSlug : game.homeTeamSlug)}
                            sx={{ 
                              textTransform: 'none',
                              justifyContent: 'flex-start',
                              fontWeight: 'bold'
                            }}
                          >
                            {isHomeTeam ? `vs ${game.awayTeamName}` : `@ ${game.homeTeamName}`}
                          </Button>

                          {/* Result */}
                          {game.homeScore !== null && game.awayScore !== null ? (
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center',
                              gap: 2,
                              p: 2,
                              bgcolor: 'background.default',
                              borderRadius: 1
                            }}>
                              <Chip
                                label={resultLabel}
                                sx={{
                                  backgroundColor: resultColor,
                                  color: 'white',
                                  fontWeight: 'bold',
                                  fontSize: '1rem',
                                  height: 40,
                                  minWidth: 40
                                }}
                              />
                              <Typography 
                                variant="h6" 
                                fontWeight="bold"
                                color={isWin ? 'success.main' : isDraw ? 'warning.main' : 'error.main'}
                              >
                                {teamScore} - {opponentScore}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="textSecondary" textAlign="center">
                              Score not available
                            </Typography>
                          )}
                        </Stack>
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
            </>
          ) : (
            <Alert severity="info">No recent games available</Alert>
          )}
        </Paper>
      </Box>

      {canManageTeam && teamData && (
        <>
          <TeamDetailsEdit
            open={isEditTeamOpen}
            teamId={teamData.teamId}
            onClose={() => setIsEditTeamOpen(false)}
            onSave={() => {
              setIsEditTeamOpen(false);
              loadTeamData();
            }}
          />

          <TeamRosterManage
            open={isRosterManageOpen}
            onClose={() => setIsRosterManageOpen(false)}
            teamId={teamData.teamId}
            teamName={teamData.teamName}
            onUpdated={loadTeamData}
          />
        </>
      )}
    </Box>
  );
};

export default TeamDetail;